import axios from "axios";
import { useState } from "react";
import {
  BoxArrowRight,
  ChevronRight,
  CreditCard,
  Lock,
} from "react-bootstrap-icons";

import { ApiConnectionString } from "../../../constants/ApiConntection";
import { useAuthContext } from "../../../hooks/useAuthContext";

import SettingsCard from "./SettingsCard";
import SectionMessage from "./SectionMessage";
import PasswordSettings from "./PasswordSettings";

import {
  createEmptyMessage,
  getErrorMessage,
} from "../settingsUtils";

export default function AccountSettings({ token }) {
  const { dispatch } = useAuthContext();

  const [isSigningOutAll, setIsSigningOutAll] = useState(false);
  const [isBillingLoading, setIsBillingLoading] = useState(false);

  const [accountMessage, setAccountMessage] =
    useState(createEmptyMessage);

  const [billingMessage, setBillingMessage] =
    useState(createEmptyMessage);

  const logoutCurrentDevice = () => {
    localStorage.removeItem("user");

    if (dispatch) {
      dispatch({ type: "LOGOUT" });
    }

    window.location.href = "/login";
  };

  const signOutAllDevices = async () => {
    const confirmed = window.confirm(
      "This will sign you out on this device and all other devices. Continue?"
    );

    if (!confirmed) {
      return;
    }

    try {
      setIsSigningOutAll(true);
      setAccountMessage(createEmptyMessage());

      const response = await axios.post(
        `${ApiConnectionString}/api/Account/SignOutAllDevices`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        logoutCurrentDevice();
        return;
      }

      setAccountMessage({
        error: "Could not sign out of all devices.",
        success: "",
      });
    } catch (error) {
      console.error(error);

      setAccountMessage({
        error: getErrorMessage(
          error,
          "Failed to sign out of all devices."
        ),
        success: "",
      });
    } finally {
      setIsSigningOutAll(false);
    }
  };

  const openBillingPortal = async () => {
    if (!token) {
      setBillingMessage({
        error: "You need to be logged in to manage your subscription.",
        success: "",
      });

      return;
    }

    try {
      setIsBillingLoading(true);
      setBillingMessage(createEmptyMessage());

      const response = await axios.get(
        `${ApiConnectionString}/api/BillingPortal`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const portalUrl = response.data?.url || response.data;

      if (!portalUrl) {
        setBillingMessage({
          error: "Could not open the billing portal.",
          success: "",
        });

        return;
      }

      window.location.href = portalUrl;
    } catch (error) {
      console.error(error);

      setBillingMessage({
        error: getErrorMessage(
          error,
          "Failed to open the billing portal."
        ),
        success: "",
      });
    } finally {
      setIsBillingLoading(false);
    }
  };

  return (
    <SettingsCard
      icon={<Lock />}
      title="Account"
      description="Manage your account and security settings."
    >
      <div className="settings-list">
        <PasswordSettings token={token} />

        {(accountMessage.error || accountMessage.success) && (
          <div className="settings-list-message">
            <SectionMessage message={accountMessage} />
          </div>
        )}

        <button
          type="button"
          className="settings-list-item"
          onClick={signOutAllDevices}
          disabled={isSigningOutAll}
        >
          <div>
            <BoxArrowRight />

            <div>
              <strong>Sign out of all devices</strong>

              <span>
                {isSigningOutAll
                  ? "Signing out..."
                  : "This will also sign you out here"}
              </span>
            </div>
          </div>

          <ChevronRight />
        </button>

        {(billingMessage.error || billingMessage.success) && (
          <div className="settings-list-message">
            <SectionMessage message={billingMessage} />
          </div>
        )}

        <button
          type="button"
          className="settings-list-item"
          onClick={openBillingPortal}
          disabled={isBillingLoading}
        >
          <div>
            <CreditCard />

            <div>
              <strong>Subscription</strong>

              <span>
                {isBillingLoading
                  ? "Opening billing portal..."
                  : "Manage your subscription and billing"}
              </span>
            </div>
          </div>

          <ChevronRight />
        </button>
      </div>
    </SettingsCard>
  );
}